import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { logEvent } from 'firebase/analytics';

import BasePageHeader, { UPageType } from '../BasePageHeader';
import { BasePageContent } from '../BasePage';
import FramedPage, { FrameBody, FrameHeader, FrameHeaderLeft, FrameHeaderRight } from '../FramedPage';
import LeftArrow from '../../components/icons/LeftArrow';
import { useBackgroundContext } from '../../contexts/BackgroundContext';
import { firebaseAnalytics } from '../../core/libs/Firebase';
import { IUEvent, IUInvitee } from '../../lib/event';
import InviteForm from '../../components/invite/InviteForm';
import CohostTable from '../../components/invite/CohostTable';

import { TAppElkAttendeeRole, TAppElkCohostRequestStatus, TAppElkInvitee } from 'TProtocol/prototypes/events/messages';
import { DeviceQueries } from 'Common/src/components/styled';
import { OutlineButton } from '../../components/buttons/OutlineButton';
import KebabMenu, { KebabMenuOption } from 'Common/src/components/KebabMenu';
import LinkIcon from '../../components/icons/LinkIcon';
import DeleteUserIcon from '../../components/icons/DeleteUserIcon';
import { useModalContext } from '../../contexts/ModalContext';
import EditInviteeModal from '../../components/modals/EditInviteeModal';
import Modal from '../../components/modals/Modal';
import { useUserContext } from '../../contexts/UserContext';
import { deletePendingInvites, editInvitee } from '../../api/ElkEventService';
import { useEventCacheContext } from '../../contexts/EventCacheContext';
import { Tooltip } from 'react-tooltip';
import { ULogApplication, ULogTag, ULogSeverity, logSumoEvent } from 'Common/src/api/SumoLogicApi';

const CohostPage = ({ event, onBackClick, onUpdate, onLinkClick, onInvalidateClick, showCopiedLinkTooptip }: {
  event?: IUEvent,
  onBackClick: () => void,
  onUpdate: (event: IUEvent, invitee: TAppElkInvitee) => Promise<void>,
  onLinkClick: () => Promise<void>,
  onInvalidateClick: () => Promise<void>,
  showCopiedLinkTooptip: boolean
}) => {
  const userContext = useUserContext();
  const backgroundContext = useBackgroundContext();
  const modalContext = useModalContext();
  const eventCacheContext = useEventCacheContext();
  const [invitees, setInvitees] = useState<IUInvitee[]>([]);

  useEffect(() => {
    if (event !== undefined) {
      document.title = `Shine Parties - Manage Cohosts - ${event.title}`;
      logEvent(firebaseAnalytics, 'page_view', {
        page_title: 'Manage Cohosts - Analytics'
      });

      backgroundContext.setColors(
        {
          colors: event?.colors,
          animationUrl: event.backgroundAnimationUrl,
          animationDisabled: event.videoBackgroundDisabled,
          subdued: true
        }
      );
      setInvitees(event.attendees);
    }
  }, [event]);

  const logUserAction = (action: string) => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.DEBUG,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: `[CohostPage] ${action}`
    });
  };

  const createCohost = async (invitee: IUInvitee) => {
    if (event !== undefined) {
      let inviteeToAdd: TAppElkInvitee;
      const existingInvitee = event.attendees.find((attendee) => attendee.inviteeId === invitee.inviteeId);
      if (existingInvitee !== undefined) {
        existingInvitee.cohostStatus = TAppElkCohostRequestStatus.PENDING;
        inviteeToAdd = existingInvitee.toTAppElkInvitee();
      } else {
        invitee.cohostStatus = TAppElkCohostRequestStatus.PENDING;
        invitees.unshift(invitee);
        inviteeToAdd = invitee.toTAppElkInvitee();
      }
      setInvitees([...invitees]);
      event.attendees = invitees;

      logUserAction(`Creating cohost, ${existingInvitee ? 'existing ' : ''}inviteeId = ${inviteeToAdd.inviteeId}`);

      await onUpdate(event, inviteeToAdd);
    }
  };

  const onMenuClick = (id: string) => {
    if (id === 'copy') {
      void onLinkClick();
    } else if (id === 'invalidate') {
      void onInvalidateClick();
    }
  };

  const menuOptions: KebabMenuOption[] = [
    {
      id: 'copy',
      label: 'Copy Cohost Link',
      icon: <LinkIcon size={20}/>
    },
    // {
    //   id: 'invalidate',
    //   label: 'Invalidate Existing Invite Link',
    //   icon: <DeleteUserIcon size={20}/>
    // }
  ];

  const onEdit = async (inviteeId: string) => {
    const invitee = invitees.find((invitee) => invitee.inviteeId === inviteeId);

    if (event && invitee) {
      modalContext.show({
        contents: <EditInviteeModal
          close={modalContext.hide}
          onSave={handleUpdate}
          event={event}
          invitee={invitee}
          emailRequired={false}
        />
      });
    }
  };

  const handleUpdate = async (invitee: IUInvitee) => {
    if (event !== undefined) {
      logUserAction(`Updating cohost, inviteeId = ${invitee.inviteeId}`);

      invitees.forEach((inv) => {
        if (inv.inviteeId === invitee.inviteeId) {
          inv.name = invitee.name;
          inv.email = invitee.email;
          inv.rsvpStatus = invitee.rsvpStatus;
          inv.inviteStatus = invitee.inviteStatus;
        }
      });
      setInvitees([...invitees]);

      modalContext.hide();
      await editInvitee(userContext, event.id, invitee.toTAppElkInvitee());
    }
  };

  const onDelete = (invitee: IUInvitee) => {
    modalContext.show({
      contents: <Modal title="Delete Invitee"
                       subtitle={`Are you sure you want to remove ${invitee.name ?? invitee.email} from the party?`}
                       primaryButtonLabel="Yes" secondaryButtonLabel="No"
                       onPrimaryClick={() => deleteInvitee(invitee.inviteeId)}
                       onSecondaryClick={modalContext.hide}
                       useSystemColors={true}/>
    });
  };

  const deleteInvitee = async (inviteId: string) => {
    if (event) {
      logUserAction(`Deleting invitee, inviteeId = ${inviteId}`);

      const newInvitees = invitees.filter((attendee) => attendee.inviteeId !== inviteId);
      event.attendees = invitees;
      setInvitees(newInvitees);
      eventCacheContext.setEvent(event);

      modalContext.hide();

      await deletePendingInvites(userContext, event.id, [inviteId]);
    }
  };

  const cohosts = event?.attendees.filter((attendee) => attendee.cohostStatus !== undefined ||
    attendee.role === TAppElkAttendeeRole.ORGANIZER) ?? [];

  return <>
    <BasePageHeader page={UPageType.Cohost}/>
    <BasePageContent $wide={true} $full={true} $centered={true}>
      <FramedPage>
        <FrameHeader>
          <FrameHeaderLeft>
            <BackArrowContainer onClick={onBackClick}>
              <LeftArrow/>
            </BackArrowContainer>
            Cohosts
          </FrameHeaderLeft>
          <FrameHeaderRight>
            <TopActionContainer>
              <Tooltip anchorSelect="#copyLink" place="top" className="copiedTooltip"
                       noArrow={true} isOpen={showCopiedLinkTooptip}>
                Copied link!
              </Tooltip>
              <OutlineButton onClick={onLinkClick} $noMin={true} id="copyLink">
                <LinkIcon size={20}/> Copy Cohost Link
              </OutlineButton>
              {/*<WhiteButton onClick={onInvalidateClick} $noMin={true}>*/}
              {/*  Invalidate Existing Invite Link*/}
              {/*</WhiteButton>*/}
            </TopActionContainer>
            <TopMobileMenu>
              <KebabMenu options={menuOptions} onClick={onMenuClick} wide={true}/>
            </TopMobileMenu>
          </FrameHeaderRight>
        </FrameHeader>
        <FrameBody>
          <InviteForm
            placeholder="Invite cohosts by name or email"
            includeAttendees={true}
            usersOnly={true}
            event={event}
            onCreate={createCohost}
          />
          { event && <CohostTable event={event} cohosts={cohosts} onEdit={onEdit} onDelete={onDelete}/> }
        </FrameBody>
      </FramedPage>
    </BasePageContent>
  </>;
};


export const BackArrowContainer = styled.div`
  cursor: pointer;
  width: 25px;
  display: flex;
  align-items: center;
`;


const TopActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (${DeviceQueries.mobile}) {
    display: none;
  }
`;

const TopMobileMenu = styled.div`
  display: none;

  @media (${DeviceQueries.mobile}) {
    display: flex;
  }
`;

export default CohostPage;
